import { z } from 'zod';
/**
 * FEJLESZTÉSEK (útiterv + változásnapló) — a `support.product-updates` [T, SHARED] busz-erőforrás
 * szerződése. Az Odin-oldali modell és az indoklás: `prisma/schema/support.prisma` → `ProductUpdate`.
 *
 * HÁROM, EGYMÁSTÓL FÜGGETLEN DIMENZIÓ (összemosni őket a modul legdrágább hibája lenne):
 *   • `stage`      — hol tart a fejlesztés → ez adja a board OSZLOPÁT, és a `shipped` viszi az idővonalra,
 *   • `status`     — publikáltuk-e (`draft` az Asana-importból és a kézi felvételből jövő állapot),
 *   • `visibility` — a PARTNER is látja-e (`public`), vagy csak a belsősök (`internal`).
 * Egy `shipped` tétel is lehet `draft` (kész, de még nem hirdettük ki), és egy `planned` tétel is lehet
 * `public` (nyilvános útiterv-ígéret).
 */
/** A pipeline-állapot — a `shipped` viszi az idővonalra, minden érték egyben egy board-oszlop (vagy a boardról lekerülő függő/elvetett). */
export const ProductUpdateStageSchema = z.enum([
    'idea',
    'planned',
    'in_progress',
    'shipped',
    'on_hold',
    'cancelled',
]);
/** A változásnapló kategória-címkéje (a bejegyzés JELLEGE). */
export const ProductUpdateTypeSchema = z.enum([
    'feature',
    'improvement',
    'fix',
    'security',
    'performance',
]);
/** PUBLIKÁCIÓ (nem a pipeline!): `draft` = csak a kezelők látják, `archived` = kivezetett bejegyzés. */
export const ProductUpdateStatusSchema = z.enum(['draft', 'published', 'archived']);
/** Kinek szól: `internal` = csak a munkatársak, `public` = a partner-portálon is megjelenik. */
export const ProductUpdateVisibilitySchema = z.enum(['internal', 'public']);
/** Az útiterv időhorizontja — a bevett „Now/Next/Later" séma. SZÁNDÉKOSAN nem dátum (lásd a modul-doksit). */
export const ProductUpdateHorizonSchema = z.enum(['now', 'next', 'later']);
/**
 * Kétnyelvű szöveg a dráton: a `hu` KÖTELEZŐ, az `en` opcionális (a felület `hu`-ra esik vissza, ha nincs).
 * MIÉRT nem a `_shared` `I18nLabelSchema`: az MINDKÉT nyelvet kötelezőnek veszi (RBAC-címkék, ahol tényleg
 * mindig van fordítás) — egy hírfolyamnál a fordítás opcionális, különben minden bejegyzést kétszer kell
 * megírni, vagy üres `en` kerül a partner szemébe.
 */
const i18nText = (max) => z.object({
    hu: z.string().trim().min(1).max(max),
    en: z.string().trim().max(max).nullish(),
});
/** Ugyanaz OLVASÁSKOR: a busz-sorban bármelyik nyelv hiányozhat (ETL/kézi adat), ezért itt megengedő. */
const i18nTextRead = z.object({ hu: z.string(), en: z.string().nullable() });
/** Egy lista-sor a belső felületen (a részlet-mezők nélkül — a lista könnyű marad). */
export const ProductUpdateRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    title: i18nTextRead,
    stage: ProductUpdateStageSchema,
    type: ProductUpdateTypeSchema,
    status: ProductUpdateStatusSchema,
    visibility: ProductUpdateVisibilitySchema,
    horizon: ProductUpdateHorizonSchema.nullable(),
    targetPeriod: z.string().nullable(),
    shippedAt: z.string().nullable(),
    publishedAt: z.string().nullable(),
    position: z.number(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    /** OCC-verzió — a szerkesztés `If-Match`-hez ezt küldi vissza. */
    version: z.number(),
});
/**
 * A részlet: a sor + a TÖRZS + a szerkesztés-nyom.
 *
 * CSATOLMÁNY-LISTA NINCS (user-döntés): a bejegyzés képeit a szerkesztő szúrja a törzsbe a globális WYSIWYG
 * kép-útján (`editor-images`) — külön fájl-feltöltés nem tartozik a modulhoz.
 */
export const ProductUpdateDetailSchema = ProductUpdateRowSchema.extend({
    /** SZANITÁLT HTML (i18n) — a repo közös rich-text pipeline-ja (`RichTextEditor` + `sanitizeRichHtml`). */
    body: i18nTextRead.nullable(),
    bodyFormat: z.literal('html'),
    source: z.enum(['manual', 'asana', 'import']),
    asanaTaskUrl: z.string().nullable(),
    /** A hívó JOGAI ezen a bejegyzésen — a felület ebből dönt, mit ajánl fel (a szerver ugyanezt érvényesíti). */
    canManage: z.boolean(),
    canPublish: z.boolean(),
    canDelete: z.boolean(),
});
/**
 * Létrehozás/szerkesztés body. A `status`/`visibility` SZÁNDÉKOSAN NINCS benne: a publikálás külön jog
 * (`product_updates.publish`) és külön végpont — enélkül a `manage` jog egy mezőn át kihirdethetne a
 * partnereknek. A `slug` opcionális: hiányában a szerver a magyar címből képzi (ütközésnél sorszámoz).
 */
export const ProductUpdateWriteBodySchema = z.object({
    slug: z
        .string()
        .trim()
        .regex(/^[a-z0-9-]{1,120}$/, 'Csak kisbetű, szám és kötőjel.')
        .optional(),
    title: i18nText(200),
    /**
     * A törzs HTML-je. A plafon a HTML-lel együtt értendő (a jegy-leírás 20 000 LÁTHATÓ karaktere mellett a
     * markup is helyet kér) — a BFF szanitál írás előtt, és a LÁTHATÓ hosszt külön ellenőrzi.
     */
    body: i18nText(50000).nullish(),
    stage: ProductUpdateStageSchema,
    type: ProductUpdateTypeSchema,
    horizon: ProductUpdateHorizonSchema.nullish(),
    targetPeriod: z.string().trim().max(40).nullish(),
    /** A kiadás üzleti dátuma (`YYYY-MM-DD`) — `shipped` stage-nél kötelező, egyébként tilos (422). */
    shippedAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Érvénytelen dátum (YYYY-MM-DD).')
        .nullish(),
    position: z.number().int().min(0).max(9999).optional(),
    asanaTaskUrl: z.string().trim().url().max(500).nullish(),
});
/**
 * PUBLIKÁLÁS body — a `visibility` a kérdés lényege: `internal` = csak a munkatársak látják,
 * `public` = a partner-portálra is kimegy. A státuszt a szerver állítja (`published` + `publishedAt`).
 * PUBLIKUS kihirdetéshez a LEÍRÁS kötelező (a partner-kártya azt mutatja) — enélkül 422.
 */
export const ProductUpdatePublishBodySchema = z.object({
    visibility: ProductUpdateVisibilitySchema,
});
/**
 * Lista-lekérdezés (belső). A `tab` a KÉT NÉZET — és ez nem adat-vágás, hanem ugyanannak a halmaznak két
 * olvasata:
 *
 * - **`board`** — a CSŐVEZETÉK: minden tétel, plusz a FRISSEN kiadottak. A kanban-oszlopok a `stage`-ből
 *   állnak, ezért a kiadott tételek is kellenek — de csak a friss vég (`SHIPPED_BOARD_DAYS`), különben a
 *   „Kiadva" oszlop évekig nőne, és a board elveszítené a jelen-fókuszt.
 * - **`timeline`** — a KIADÁS-TÖRTÉNET: kizárólag a `shipped` tételek, kiadás szerint csökkenően.
 *
 * MIÉRT VÁLTOZOTT (user-döntés): korábban `roadmap`/`changelog` volt, és a felületen két fül két TÁBLÁT
 * mutatott — két adathalmaznak látszott, miközben egy folyamat. (Ráadásul élő adaton mérve a
 * változásnapló-fül ÜRES volt: 47 tételből nulla `shipped`.) A nézet-váltás ugyanazt a halmazt mutatja
 * kétféleképp: a board azt, hogy MI VAN FOLYAMATBAN, az idővonal azt, hogy MI KÉSZÜLT EL és MIKOR.
 */
export const ProductUpdateListQuerySchema = z.object({
    tab: z.enum(['board', 'timeline']).optional(),
    /** Szabad szöveg: CÍM és slug (a törzs szándékosan nem — a HTML-markup zajt adna). */
    q: z.string().trim().max(200).optional(),
    stage: ProductUpdateStageSchema.optional(),
    type: ProductUpdateTypeSchema.optional(),
    status: ProductUpdateStatusSchema.optional(),
    visibility: ProductUpdateVisibilitySchema.optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
});
export const ProductUpdateListResponseSchema = z.object({
    data: z.array(ProductUpdateRowSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    /** A fül-jelvények: hány tétel van a másik fülön (a szűrők nélkül számolva). */
    /**
     * A NÉZET-JELVÉNYEK számai, a szűrőktől FÜGGETLENÜL (a „hány van a másik nézeten" kérdésre a szűrt szám
     * félrevezető lenne). A `board` a folyamatban lévő tételeké, a `timeline` a kiadás-történeté, a `draft`
     * pedig a kihirdetésre váróké — ez utóbbi a kezelői teendő, ezért van külön.
     */
    counts: z.object({ board: z.number(), timeline: z.number(), draft: z.number() }),
});
// ── PARTNER-oldal ────────────────────────────────────────────────────────────────────────────────────
/**
 * A partner CSAK a `status=published` + `visibility=public` bejegyzéseket látja, és jóval kevesebb mezőt:
 * nincs `status`/`visibility` (számára minden látható tétel „kiadott"), nincs `position`, nincs OCC-verzió,
 * és nincs Asana-hivatkozás sem (belső eszköz). A KÉPEK a törzsben élnek (a BFF friss presigned URL-re
 * cseréli a belső kép-utakat) — külön kép-lista nincs. A szűrés a BFF-en, a gépi identityvel olvasott adaton fut.
 */
export const PortalProductUpdateRowSchema = z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    /** A törzs a hívó nyelvén — a beágyazott képek FRISS presigned URL-lel (a BFF cseréli ki). */
    body: z.string().nullable(),
    /**
     * A BELSŐ FÁZIS (`stage`) SZÁNDÉKOSAN NINCS ITT.
     *
     * A partner a HORIZONTOT látja (Most / Következik / Később), nem a belső folyamatot — az `on_hold` és a
     * `cancelled` fázis olyan információ, amit nem neki szánunk. Korábban a mező kiment (a partner-lap nem
     * jelenítette meg, de a hálózati válaszban ott volt), miközben a modul-doksi §6 épp azt mondta ki, hogy
     * a belső fázis „kiszivárogtatná a folyamatot".
     */
    type: ProductUpdateTypeSchema,
    horizon: ProductUpdateHorizonSchema.nullable(),
    targetPeriod: z.string().nullable(),
    shippedAt: z.string().nullable(),
    publishedAt: z.string().nullable(),
});
export const PortalProductUpdateListResponseSchema = z.object({
    /** A Változásnapló (kiadott tételek, `shippedAt`/`publishedAt` szerint csökkenően). */
    changelog: z.array(PortalProductUpdateRowSchema),
    /** Az Útiterv (még nem kiadott, publikus tételek — horizont szerint csoportosítva a felületen). */
    roadmap: z.array(PortalProductUpdateRowSchema),
    /** `true`, ha a lista a gépi kredenciál hiánya miatt nem szolgálható ki (a felület ezt KIMONDJA). */
    unavailable: z.boolean().optional(),
});
//# sourceMappingURL=schemas.js.map